import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { TokenService } from '../services/token.service';
import { LoginService } from '../../pages/login/login.service';
import { Router } from '@angular/router';
import { NetworkStatusNotifierService } from '../services/network-status-notifier.service';
import { AccountStorageResetService } from '../services/account-storage-reset.service';
import { SocketService } from '../services/socket.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  constructor(
    private tokenService: TokenService,
    private authService: LoginService,
    private router: Router,
    private networkStatusNotifier: NetworkStatusNotifierService,
    private accountStorageReset: AccountStorageResetService,
    private socketService: SocketService,
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const token = this.tokenService.getToken();
    let authReq = req;

    if (token) {
      authReq = req.clone({
        setHeaders: { Authorization: `Bearer ${token}` },
      });
    }

    return next.handle(authReq).pipe(
      catchError((error: any) => {
        if (error instanceof HttpErrorResponse) {
          this.networkStatusNotifier.reportHttpError(error);
        }

        if (error instanceof HttpErrorResponse && error.status === 401) {
          const url = req.url || '';
          const isAuthEndpoint = /\/auth\/(login|otp|refresh)/.test(url);
          const shouldBypassSessionLogout =
            /\/users\/resetPassword(?:[/?#]|$)/.test(url);

          if (!isAuthEndpoint && !shouldBypassSessionLogout) {
            return this.handle401Error(authReq, next);
          }
        }
        return throwError(() => error);
      })
    );
  }

  private handle401Error(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const refreshToken = this.tokenService.getRefreshToken();
      if (!refreshToken) {
        this.logoutToLogin();
        return throwError(() => new Error('No refresh token'));
      }

      return this.authService.refresh(refreshToken).pipe(
        switchMap((res: any) => {
          this.isRefreshing = false;
          const newAccess = res?.data?.accessToken;
          if (newAccess) {
            this.tokenService.setTokens(newAccess);
            this.refreshTokenSubject.next(newAccess);
            const retryReq = req.clone({ setHeaders: { Authorization: `Bearer ${newAccess}` } });
            return next.handle(retryReq);
          }
          this.logoutToLogin();
          return throwError(() => new Error('Failed to refresh token'));
        }),
        catchError(err => {
          this.isRefreshing = false;
          this.logoutToLogin();
          return throwError(() => err);
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter(token => !!token),
        take(1),
        switchMap((token) => {
          const retryReq = req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
          return next.handle(retryReq);
        })
      );
    }
  }

  private logoutToLogin() {
    this.socketService.disconnect();
    void this.accountStorageReset
      .resetAccountData()
      .then(() => this.router.navigate(['/login']));
  }
}

